import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { toastr } from 'react-redux-toastr';

import { useAdmin } from './AdminContext';
import { adminApi, can } from './adminApi';
import { ActivityPanel, ConfirmDialog, ReasonDialog, TemporaryPasswordDialog } from './dialogs';
import { ActionsMenu, DescList, EmptyState, PageHeader, Panel } from './kit';
import { formatDocument } from './ui';

// Pagina da oficina: cadastro, acesso ao Sobre Rodas e atividade.
export default function CompanyPage() {
  const { cnpj } = useParams();
  const { admin } = useAdmin();
  const [company, setCompany] = useState(null);
  const [failed, setFailed] = useState(false);
  const [email, setEmail] = useState(null);
  const [dialog, setDialog] = useState(null);
  const [activityKey, setActivityKey] = useState(0);

  useEffect(() => {
    let active = true;
    setCompany(null);
    setEmail(null);
    adminApi.company(cnpj).then((data) => { if (active) setCompany(data); }).catch(() => { if (active) setFailed(true); });
    return () => { active = false; };
  }, [cnpj]);

  const crumbs = [{ label: 'Oficinas', to: '/admin/companies' }];
  const done = () => setActivityKey((key) => key + 1);

  if (failed) {
    return (
      <div className='ax-page'>
        <PageHeader crumbs={crumbs} title='Oficina' />
        <EmptyState title='Oficina não encontrada' action={<Link className='ax-btn' to='/admin/companies'>Voltar para oficinas</Link>} />
      </div>
    );
  }
  if (!company) {
    return (
      <div className='ax-page'>
        <PageHeader crumbs={crumbs} title='Carregando…' />
        <span className='ax-skeleton-block' aria-busy='true' />
      </div>
    );
  }

  const name = company.tradingName || company.name || 'Oficina';
  const menu = [
    company.hasEmail && !email && { label: 'Ver e-mail completo', hint: 'Pede motivo', onSelect: () => setDialog('reveal') },
    can(admin, 'support_agent') && { label: 'Enviar link de acesso', disabled: !company.hasEmail, hint: company.hasEmail ? null : 'Sem e-mail cadastrado', onSelect: () => setDialog('link') },
    can(admin, 'super_admin') && { label: 'Gerar senha provisória', danger: true, onSelect: () => setDialog('temp') },
  ];

  return (
    <div className='ax-page'>
      <PageHeader crumbs={crumbs} title={name} meta={`${company.origin} · CNPJ ${formatDocument(company.cpfCnpj)}`} actions={<ActionsMenu items={menu} />} />

      {company.mustChangePassword && <p className='ax-alert is-warn'>Senha provisória ativa: a oficina vai criar uma senha nova no próximo acesso.</p>}

      <div className='ax-grid'>
        <Panel title='Cadastro' action={email ? <span className='ax-badge'>E-mail completo · registrado</span> : null}>
          <DescList items={[
            { label: 'Nome fantasia', value: company.tradingName },
            { label: 'Razão social', value: company.name },
            { label: 'CNPJ', value: formatDocument(company.cpfCnpj) },
            { label: 'E-mail', value: email || company.email },
            { label: 'Origem', value: company.origin },
          ]} />
        </Panel>
        <Panel title='No Sobre Rodas'>
          <DescList items={[
            { label: 'Acesso', value: company.hasAccess ? 'Senha criada' : 'Ainda sem senha' },
            { label: 'Avaliações', value: String(company.evaluations) },
          ]} />
        </Panel>
      </div>

      <ActivityPanel targetType='company' targetId={company.cpfCnpj} refreshKey={activityKey} />

      {dialog === 'reveal' && (
        <ReasonDialog
          title='Ver e-mail completo?'
          text={`O e-mail de ${name} aparece sem máscara nesta página.`}
          confirmLabel='Ver e-mail'
          onConfirm={async (reason) => { const data = await adminApi.companyReveal(cnpj, reason); setEmail(data.email); done(); }}
          onClose={() => setDialog(null)}
        />
      )}
      {dialog === 'link' && (
        <ConfirmDialog
          title={`Enviar link de acesso para ${name}?`}
          text='O e-mail cadastrado recebe um link para criar ou redefinir a senha. A senha atual continua valendo até ser trocada.'
          confirmLabel='Enviar link'
          onConfirm={async () => { const { sentTo } = await adminApi.companyResetLink(cnpj); toastr.success('Link enviado.', `Para ${sentTo}.`); done(); }}
          onClose={() => setDialog(null)}
        />
      )}
      {dialog === 'temp' && (
        <TemporaryPasswordDialog
          targetName={name}
          onGenerate={async (reason, password) => { const result = await adminApi.companyTemporaryPassword(cnpj, reason, password); done(); return result; }}
          onClose={() => setDialog(null)}
        />
      )}
    </div>
  );
}
